import { Link } from "react-router-dom";
import { Calendar, Bookmark, Mail, Heart, ArrowRight, BookOpen, Sparkles } from "lucide-react";

/**
 * Magazine-style "What's New" tile row sitting above the dashboard tabs.
 *
 * Four tiles:
 *   1. Featured     — large hero card with most recent post / next event
 *   2. My Portal    — dark "LIVE" announcement
 *   3. Events       — calendar nudge
 *   4. Suggest Box  — sealed envelope w/ heart
 */
const Pill = ({ children, color = "#FF2D87", textColor = "white" }) => (
  <span
    className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.25em] px-2.5 py-1 rounded-full"
    style={{ background: color, color: textColor }}
  >
    {children}
  </span>
);

const WhatsNewTiles = ({ featured, onSuggestClick }) => {
  return (
    <section className="mb-12" data-testid="whats-new-tiles">
      <div className="flex items-baseline justify-between mb-5">
        <h2 className="text-2xl md:text-3xl font-display font-bold text-[#0A0A0A]">What's new</h2>
        <Link to="/blog" className="text-xs font-bold uppercase tracking-widest text-[#5170FF] hover:text-[#FF2D87] inline-flex items-center gap-1">
          All resources <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-5 auto-rows-min">
        {/* 1. FEATURED — large, spans 2 cols + 2 rows on lg */}
        {featured ? (
          <Link
            to={featured.href || "#"}
            className="group md:col-span-2 lg:col-span-2 lg:row-span-2 relative overflow-hidden border-2 border-[#0A0A0A] hover:-translate-y-1 transition-all flex flex-col min-h-[340px] lg:min-h-[480px]"
            style={{ boxShadow: "6px 6px 0 #0A0A0A" }}
            data-testid="whats-new-featured"
          >
            <div className="relative flex-1 overflow-hidden" style={{ background: featured.accent || "#FF2D87" }}>
              {featured.image_url ? (
                <img
                  src={featured.image_url}
                  alt={featured.title}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center">
                  {featured.kind === "event"
                    ? <Calendar className="w-32 h-32 text-white/30" />
                    : <BookOpen className="w-32 h-32 text-white/30" />}
                </div>
              )}
              <div className="absolute top-4 left-4">
                <Pill color="#0A0A0A" textColor="#FF2D87">★ {featured.label || "Latest"}</Pill>
              </div>
            </div>
            <div className="bg-white p-5 md:p-6 border-t-2 border-[#0A0A0A]">
              <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#FF2D87] mb-2">
                {featured.kind === "post" ? "Resources · New article" : "Events · Coming up"}
              </p>
              <h3 className="text-xl md:text-2xl font-display font-bold leading-tight mb-2 text-[#0A0A0A] group-hover:text-[#FF2D87] transition-colors">
                {featured.title}
              </h3>
              {featured.excerpt && (
                <p className="text-sm text-[#4A4A4A] line-clamp-2">{featured.excerpt}</p>
              )}
              <span className="mt-3 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest text-[#0A0A0A] group-hover:text-[#FF2D87]">
                {featured.kind === "post" ? "Read article" : "Save my spot"} <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </div>
          </Link>
        ) : (
          <div
            className="md:col-span-2 lg:col-span-2 lg:row-span-2 bg-[#FFC2E8] border-2 border-[#0A0A0A] p-8 flex flex-col justify-center min-h-[340px]"
            style={{ boxShadow: "6px 6px 0 #0A0A0A" }}
            data-testid="whats-new-featured-empty"
          >
            <Pill color="#0A0A0A" textColor="#FF2D87">★ Fresh ink, soon</Pill>
            <h3 className="text-2xl font-display font-bold mt-3 text-[#0A0A0A] leading-tight">
              We're working on something good.
            </h3>
            <p className="text-sm text-[#4A4A4A] mt-2">Our first feature stories and events are being prepped — check back soon, or browse what's already live.</p>
            <div className="flex flex-wrap gap-2 mt-4">
              <Link to="/events" className="bg-[#0A0A0A] text-white text-[10px] font-bold uppercase tracking-widest rounded-full px-4 py-2 hover:bg-white hover:text-[#0A0A0A] border-2 border-[#0A0A0A] transition-colors">Browse events</Link>
              <Link to="/blog" className="bg-white text-[#0A0A0A] text-[10px] font-bold uppercase tracking-widest rounded-full px-4 py-2 hover:bg-[#0A0A0A] hover:text-white border-2 border-[#0A0A0A] transition-colors">Browse resources</Link>
            </div>
          </div>
        )}

        {/* 2. MY PORTAL — dark tile */}
        <Link
          to="/dashboard"
          onClick={() => { setTimeout(() => document.querySelector('[data-testid="tab-pinned"]')?.click(), 50); }}
          className="group relative bg-[#0A0A0A] text-white border-2 border-[#0A0A0A] p-5 md:p-6 flex flex-col gap-3 transition-all hover:-translate-y-1 min-h-[220px]"
          style={{ boxShadow: "5px 5px 0 #FF2D87" }}
          data-testid="whats-new-portal"
        >
          <Pill color="#FF2D87" textColor="white">New · Live now</Pill>
          <div className="w-14 h-14 bg-[#FF2D87] border-2 border-white flex items-center justify-center">
            <Bookmark className="w-7 h-7 text-white" />
          </div>
          <h3 className="text-lg font-display font-bold leading-tight text-white">
            My Portal is now <span className="text-[#FF2D87]">LIVE</span>
          </h3>
          <p className="text-sm text-white/70 leading-snug">Find all your pins, saved members, businesses, events and matches in one place.</p>
          <span className="mt-auto inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest text-[#FFC2E8] group-hover:text-white">
            Open My Portal <ArrowRight className="w-3.5 h-3.5" />
          </span>
        </Link>

        {/* 3. EVENTS — calendar */}
        <Link
          to="/events"
          className="group bg-white border-2 border-[#0A0A0A] p-5 md:p-6 flex flex-col gap-3 transition-all hover:-translate-y-1 min-h-[220px]"
          style={{ boxShadow: "5px 5px 0 #5170FF" }}
          data-testid="whats-new-events"
        >
          <Pill color="#5170FF" textColor="white">Upcoming</Pill>
          <div className="w-14 h-14 bg-[#5170FF] border-2 border-[#0A0A0A] flex items-center justify-center">
            <Calendar className="w-7 h-7 text-white" />
          </div>
          <h3 className="text-lg font-display font-bold leading-tight text-[#0A0A0A]">
            Recent <span className="text-[#5170FF]">events</span>
          </h3>
          <p className="text-sm text-[#4A4A4A] leading-snug">Make sure you check out what's coming up — workshops, meetups and the monthly gathering.</p>
          <span className="mt-auto inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest text-[#0A0A0A] group-hover:text-[#5170FF]">
            See the calendar <ArrowRight className="w-3.5 h-3.5" />
          </span>
        </Link>

        {/* 4. SUGGESTION BOX */}
        <button
          type="button"
          onClick={onSuggestClick}
          className="group text-left bg-white border-2 border-[#0A0A0A] p-5 md:p-6 flex flex-col gap-3 transition-all hover:-translate-y-1 min-h-[220px]"
          style={{ boxShadow: "5px 5px 0 #FF2D87" }}
          data-testid="whats-new-suggest"
        >
          <Pill color="#FFC2E8" textColor="#0A0A0A">Suggestion box</Pill>
          <div className="relative w-14 h-14 bg-[#FFC2E8] border-2 border-[#0A0A0A] flex items-center justify-center">
            <Mail className="w-7 h-7 text-[#0A0A0A]" strokeWidth={2.2} />
            <Heart className="w-4 h-4 text-[#FF2D87] absolute -bottom-1.5 -right-1.5 fill-[#FF2D87] drop-shadow-md" />
          </div>
          <h3 className="text-lg font-display font-bold leading-tight text-[#0A0A0A]">
            Have an idea <span className="text-[#FF2D87]">for us?</span>
          </h3>
          <p className="text-sm text-[#4A4A4A] leading-snug">An idea, a bug, a question? We want to hear from you. Send it privately to the team.</p>
          <span className="mt-auto inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest text-[#FF2D87] group-hover:text-[#0A0A0A]">
            <Sparkles className="w-3.5 h-3.5" /> Drop a note
          </span>
        </button>
      </div>
    </section>
  );
};

export default WhatsNewTiles;
